import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import type { ActivityStatus } from 'api/recoveryAi.types'
import FeedbackStatusBadge from 'components/FeedbackStatusBadge'
import ArrowLeft from 'components/Icons/ArrowLeft'
import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import Spinner from 'components/Spinner'
import { buildTranslate } from 'locales'
import React from 'react'
import { Link } from 'react-router-dom'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import RatingPager from './RatingPager'
import { RatingDetailActions, RatingDetailPager } from './useRatingDetail'

const t = buildTranslate('order_ratings.rating_detail')

type Props = {
  backTo: string
  badgeStatus: ActivityStatus
  title: string
  // While the order snapshot is loading, `title` still holds its customerName
  // fallback — rendering it would flash the customer's name into the header for
  // a beat before the order number lands. Show a placeholder instead.
  isTitleLoading: boolean
  subtitle: string
  pager: RatingDetailPager | null
  // The Grant / Send / Mark row is hidden once the rating is resolved.
  showActions: boolean
  actions: RatingDetailActions
}

// Full-bleed white header band with a bottom divider (Option B): the band
// spans the viewport; its inner container keeps the header content aligned with
// the body cards below. Purely presentational — every value and callback is fed
// by useRatingDetail, so this renders without Redux or the router's data.
const RatingDetailHeader = ({
  backTo,
  badgeStatus,
  title,
  isTitleLoading,
  subtitle,
  pager,
  showActions,
  actions,
}: Props) => {
  const [css] = useStyletron()

  return (
    <div
      className={css({
        backgroundColor: 'white',
        borderBottom: `1px solid ${merchantTheme.colors.grey20}`,
      })}
    >
      <div
        className={`container ${css({
          paddingTop: '3rem',
          paddingBottom: '1.5rem',
        })}`}
      >
        {/* Top row: back link left, unresolved pager right (Option B). */}
        <div
          className={`d-flex align-items-center justify-content-between ${css({
            gap: '16px',
          })}`}
        >
          <Link
            to={backTo}
            className={`d-flex align-items-center ${css({
              width: 'fit-content',
              // Dark, not the default link blue (Option B). The arrow inherits
              // this via currentColor. `:hover` restates both to override
              // Bootstrap's `a:hover` (blue + underline).
              color: merchantTheme.colors.grey90,
              fontWeight: 700,
              textDecoration: 'none',
              gap: '8px',
              ':hover': {
                color: merchantTheme.colors.grey90,
                textDecoration: 'none',
              },
            })}`}
          >
            {/* Decorative — the link text already conveys the action. */}
            <span aria-hidden="true" className={css({ display: 'flex' })}>
              <ArrowLeft />
            </span>
            {t('back')}
          </Link>

          {pager && (
            <RatingPager
              position={pager.position}
              total={pager.total}
              hasPrev={pager.hasPrev}
              hasNext={pager.hasNext}
              onPrev={pager.onPrev}
              onNext={pager.onNext}
            />
          )}
        </div>

        <div
          className={`d-flex align-items-start justify-content-between ${css({
            marginTop: '12px',
            gap: '16px',
          })}`}
        >
          <div>
            <div className={css({ marginBottom: '8px' })}>
              <FeedbackStatusBadge
                status={badgeStatus}
                variant="order-rating"
              />
            </div>
            {isTitleLoading ? (
              // Same typography wrapper as the real title, with the tag swapped
              // to a div: it reserves the identical line box and margin, so the
              // subtitle below doesn't jump when the title lands — and a
              // spinner is not a heading, so it should not render as one.
              // role/aria-label go on the outer element because uikit's Text
              // forwards only tag/typography/color/bold/className.
              <div role="status" aria-label={t('order_heading_loading')}>
                <Text.Header3 tag="div">
                  <Spinner isLoading />
                </Text.Header3>
              </div>
            ) : (
              <Text.Header3>{title}</Text.Header3>
            )}
            <Text.BodyText4 color="grey70">{subtitle}</Text.BodyText4>
          </div>

          {/* Operator actions, in the case detail's header slot and order — minus
          "Restrict customer", which has no meaning without a RecoveryAI
          conversation. The row disappears once the rating is resolved; for a
          viewer without manage-Feedback it stays visible but DISABLED (the
          deliberate deviation from CaseDetail). "Send message" sits between Grant
          reward and Mark as resolved (the mockup's 3-button row). */}
          {showActions && (
            <div className={`d-flex ${css({ gap: '12px' })}`}>
              {/* `inline` on every wrapper: PermissionTooltip defaults its target
                  span to `d-block w-100`, and inside this flex row that makes
                  every button a full-width flex item — they then compete for the
                  same 100%, get shrunk, and the longer label ("Mark as resolved")
                  wraps onto two lines. Inline keeps each button its natural width. */}
              <PermissionTooltip
                disabled={actions.isGrantPermissionMissing}
                reason={actions.grantReason}
                id="grant-reward-permission"
                placement="top"
                inline
              >
                <Button
                  kind="secondary"
                  onClick={actions.onGrant}
                  disabled={actions.isGrantDisabled}
                >
                  {t('actions.grant_reward')}
                </Button>
              </PermissionTooltip>
              <PermissionTooltip
                disabled={!actions.canManageFeedback}
                reason={actions.feedbackPermissionReason}
                id="send-message-permission"
                placement="top"
                inline
              >
                <Button
                  kind="secondary"
                  onClick={actions.onSendMessage}
                  disabled={actions.isSendMessageDisabled}
                >
                  {t('actions.send_message')}
                </Button>
              </PermissionTooltip>
              <PermissionTooltip
                disabled={!actions.canManageFeedback}
                reason={actions.feedbackPermissionReason}
                id="mark-resolved-permission"
                placement="top"
                inline
              >
                <Button
                  kind="primary"
                  onClick={actions.onMarkResolved}
                  disabled={actions.isResolveDisabled}
                >
                  {t('actions.mark_resolved')}
                </Button>
              </PermissionTooltip>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default RatingDetailHeader
